import React from 'react';

import PropTypes from 'prop-types';
import { Typography } from '@telus-uds/components-web';

import { useLocalizedStringsContext } from '../context/LocalizedStringsContext';
import {
  SummaryRow,
  SummaryLabel,
  SummaryValue,
  SummarySectionHeading
} from './css';

const Summary = ({ orderData }) => {
  const {
    contractTerm,
    typeOfInstallation: typeOfInstallationValue,
    businessConnectEdition,
    starterSeat: starterSeatValue,
    officeSeats: officeSeatsValue,
    additionalSpareDevices,
    devices,
    features: featuresList,
    howCustomerWantToPayForWhiteGlove,
    numberOfSeatsThatRequireWhiteGlove,
    numberOfSeatsThatRequireWhiteGloveAI,
    doesTheCustomerRequireWhiteGlove,
    isThisAnExistingGSuiteCustomer,
    optOutFromGSuiteBusinessLicenses,
    gSuiteComplimentarySeats,
    additionalGSuiteBusinessLicences,
    gSuiteInstallAndSetup,
    gSuiteEmailMigrationServices
  } = orderData;
  const localizedStrings = useLocalizedStringsContext();
  const {
    ORDERS: { ORDER_SUMMARY },
    contentfulValues: { yes, no, valueEasyPayment, valuePaymentUpfront },
    orderTracking: {
      summaryLabels: {
        term,
        typeOfInstallation,
        edition,
        starterSeat,
        officeSeat,
        primaryDevices,
        additionalDevices,
        features
      },
      labelDeviceName,
      labelFeatureName,
      labelQuantity,
      wgUpfrontSocForSite,
      wgUpfrontSocForUser,
      wgEasypaymentSocForUser,
      wgEasypaymentSocForUserOdysseyAI,
      wgUpfrontSocForUserOdysseyAI,
      gsuiteTotalLicences,
      gsuiteTotalLicencesIncluded,
      gsuiteInstallAndSetupIncluded,
      gsuiteEmailMigration
    }
  } = localizedStrings;

  /**
   * Add WG to the features list
   */
  if (doesTheCustomerRequireWhiteGlove === yes) {
    const wgAiQty = Number(numberOfSeatsThatRequireWhiteGloveAI) || 0;
    if (howCustomerWantToPayForWhiteGlove === valueEasyPayment) {
      featuresList.push({
        description: wgEasypaymentSocForUser,
        quantity: numberOfSeatsThatRequireWhiteGlove
      });
      if (wgAiQty > 0) {
        featuresList.push({
          description: wgEasypaymentSocForUserOdysseyAI,
          quantity: wgAiQty
        });
      }
    }
    if (howCustomerWantToPayForWhiteGlove === valuePaymentUpfront) {
      featuresList.push({
        description: wgUpfrontSocForSite,
        quantity: 1
      });
      featuresList.push({
        description: wgUpfrontSocForUser,
        quantity: numberOfSeatsThatRequireWhiteGlove
      });
      if (wgAiQty > 0) {
        featuresList.push({
          description: wgUpfrontSocForUserOdysseyAI,
          quantity: wgAiQty
        });
      }
    }
  }

  /**
   * Add GSuite to the features list
   */
  if (
    isThisAnExistingGSuiteCustomer === no &&
    !optOutFromGSuiteBusinessLicenses
  ) {
    featuresList.push({
      description: gsuiteTotalLicencesIncluded,
      quantity: gSuiteComplimentarySeats
    });
    if (additionalGSuiteBusinessLicences)
      featuresList.push({
        description: gsuiteTotalLicences,
        quantity: additionalGSuiteBusinessLicences
      });
    if (gSuiteInstallAndSetup === yes)
      featuresList.push({
        description: gsuiteInstallAndSetupIncluded,
        quantity: 1
      });
    if (gSuiteEmailMigrationServices)
      featuresList.push({
        description: gsuiteEmailMigration,
        quantity: gSuiteEmailMigrationServices
      });
  }

  return (
    <>
      {orderData && (
        <>
          <Typography heading="h2" variant={{ size: 'h2' }} block>
            {ORDER_SUMMARY}
          </Typography>
          <div style={{ marginTop: '2rem' }}>
            <SummaryRow>
              <SummaryLabel>{term}</SummaryLabel>
              <SummaryValue>{contractTerm}</SummaryValue>
            </SummaryRow>
            <SummaryRow>
              <SummaryLabel>{typeOfInstallation}</SummaryLabel>
              <SummaryValue>{typeOfInstallationValue}</SummaryValue>
            </SummaryRow>
            <SummaryRow>
              <SummaryLabel>{edition}</SummaryLabel>
              <SummaryValue>{businessConnectEdition}</SummaryValue>
            </SummaryRow>
            <SummaryRow>
              <SummaryLabel>{starterSeat}</SummaryLabel>
              <SummaryValue>{starterSeatValue}</SummaryValue>
            </SummaryRow>
            <SummaryRow>
              <SummaryLabel>{officeSeat}</SummaryLabel>
              <SummaryValue>{officeSeatsValue}</SummaryValue>
            </SummaryRow>
          </div>
          {devices && devices.length > 0 && (
            <div style={{ marginTop: '2rem' }}>
              <SummarySectionHeading>{primaryDevices}</SummarySectionHeading>
              <table className="table table-bordered">
                <thead style={{ background: '#f7f7f8' }}>
                  <tr>
                    <th width="20%" scope="col">
                      #
                    </th>
                    <th width="60%" scope="col">
                      {labelDeviceName}
                    </th>
                    <th width="20%" scope="col">
                      {labelQuantity}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {devices.map((device, index) => (
                    <tr>
                      <th scope="row">{index + 1}</th>
                      <td>{device.name}</td>
                      <td>{device.quantity}</td>
                    </tr>
                  ))}
                  {}
                </tbody>
              </table>
            </div>
          )}
          {additionalSpareDevices && additionalSpareDevices.length > 0 && (
            <div style={{ marginTop: '2rem' }}>
              <SummarySectionHeading>{additionalDevices}</SummarySectionHeading>
              <table className="table table-bordered">
                <thead style={{ background: '#f7f7f8' }}>
                  <tr>
                    <th width="20%" scope="col">
                      #
                    </th>
                    <th width="60%" scope="col">
                      {labelDeviceName}
                    </th>
                    <th width="20%" scope="col">
                      {labelQuantity}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {additionalSpareDevices.map((device, index) => (
                    <tr>
                      <th scope="row">{index + 1}</th>
                      <td>{device.name}</td>
                      <td>{device.quantity}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {featuresList && featuresList.length > 0 && (
            <div style={{ marginTop: '2rem' }}>
              <SummarySectionHeading>{features}</SummarySectionHeading>
              <table className="table table-bordered">
                <thead style={{ background: '#f7f7f8' }}>
                  <tr>
                    <th width="20%" scope="col">
                      #
                    </th>
                    <th width="60%" scope="col">
                      {labelFeatureName}
                    </th>
                    <th width="20%" scope="col">
                      {labelQuantity}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {featuresList.map((device, index) => (
                    <tr>
                      <th scope="row">{index + 1}</th>
                      <td>{device.description}</td>
                      <td>{device.quantity}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </>
  );
};

Summary.propTypes = {
  orderData: PropTypes.objectOf(PropTypes.string).isRequired
};

export default Summary;
